// src/pages/Dashboard.js
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import api from '../lib/api'

// ── Constants ─────────────────────────────────────────────────────────────

// Start of today (midnight local) — used for overdue / upcoming calculations
const TODAY = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d })()

const STAGE_BADGE = {
  'REVIEWING':       'badge-gray',
  'IN DISCUSSIONS':  'badge-blue',
  'NEGOTIATING EL':  'badge-yellow',
  'LIVE':            'badge-green',
  'ON HOLD':         'badge-yellow',
  'PASSED':          'badge-red',
}

const TYPE_BADGE = {
  'Email':    'badge-blue',
  'Meeting':  'badge-purple',
  'Call':     'badge-green',
  'Note':     'badge-gray',
  'Event':    'badge-yellow',
}

// ── Helpers ───────────────────────────────────────────────────────────────

function formatRaise(amount) {
  if (amount == null) return '—'
  if (amount >= 1_000_000_000) return `$${(amount / 1_000_000_000).toFixed(1)}B`
  if (amount >= 1_000_000)     return `$${Math.round(amount / 1_000_000)}M`
  return `$${amount.toLocaleString()}`
}

function formatDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatDateTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' · ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

function daysOverdue(dateStr) {
  const due = new Date(dateStr)
  due.setHours(0, 0, 0, 0)
  const diff = Math.floor((TODAY - due) / 86_400_000)
  if (diff === 0) return 'Due today'
  return `${diff}d overdue`
}

function ownerLabel(uuid) {
  if (!uuid) return '—'
  return uuid.slice(0, 8) + '…'
}

// ── Section: Active Mandates ──────────────────────────────────────────────

function ActiveMandates({ mandates, isLoading }) {
  return (
    <section className="card" style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
      <SectionHeader title="Active Mandates" count={!isLoading ? mandates.length : null} />

      {isLoading ? (
        <LoadingBox />
      ) : mandates.length === 0 ? (
        <EmptyBox text="No active mandates" sub="Mandates with Active status will appear here." />
      ) : (
        <div>
          {mandates.map((m, i) => (
            <Link
              key={m.mandate_id}
              to={`/mandates/${m.mandate_id}`}
              style={{ textDecoration: 'none' }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '13px 20px',
                  borderBottom: i < mandates.length - 1 ? '1px solid var(--border)' : 'none',
                  transition: 'background 0.1s',
                  cursor: 'pointer',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-raised)')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}
              >
                {/* Name + GP */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: '600',
                    fontSize: '14px',
                    color: 'var(--text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginBottom: '3px',
                  }}>
                    {m.mandate_name}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {m.gp_firm?.name ?? '—'}
                    {m.relationship_owner && (
                      <span style={{ marginLeft: '10px', color: 'var(--text-muted)' }}>
                        Owner: {ownerLabel(m.relationship_owner)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Badges */}
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0, alignItems: 'center' }}>
                  <span className={`badge ${STAGE_BADGE[m.bd_stage] ?? 'badge-gray'}`}>
                    {m.bd_stage}
                  </span>
                  <span className={`badge ${m.coverage_type === 'Partnered' ? 'badge-blue' : 'badge-gray'}`}>
                    {m.coverage_type ?? 'Solo'}
                  </span>
                </div>

                {/* Target raise */}
                <div style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: 'var(--text-secondary)',
                  flexShrink: 0,
                  minWidth: '56px',
                  textAlign: 'right',
                }}>
                  {formatRaise(m.target_raise)}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  )
}

// ── Section: Follow-up Alerts ─────────────────────────────────────────────

function FollowUpAlerts({ items, isLoading }) {
  return (
    <section className="card" style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
      <SectionHeader
        title="Follow-up Alerts"
        count={!isLoading && items.length > 0 ? items.length : null}
        countClass="badge-red"
      />

      {isLoading ? (
        <LoadingBox />
      ) : items.length === 0 ? (
        <EmptyBox
          text="All caught up"
          sub="No overdue follow-ups right now."
          icon={
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          }
        />
      ) : (
        <div>
          {items.map((i, idx) => (
            <div
              key={i.interaction_id}
              style={{
                padding: '12px 20px',
                borderBottom: idx < items.length - 1 ? '1px solid var(--border)' : 'none',
              }}
            >
              <div style={{
                fontSize: '13px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                marginBottom: '4px',
              }}>
                {i.subject_title || `${i.type} interaction`}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {i.mandate?.mandate_name ?? 'No mandate'}
                </span>
                <span
                  className="badge badge-red"
                  style={{ flexShrink: 0, fontSize: '10px', letterSpacing: '0.03em' }}
                >
                  {daysOverdue(i.follow_up_due)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

// ── Section: Upcoming Action Items ────────────────────────────────────────

function UpcomingActionItems({ items, isLoading }) {
  return (
    <section className="card" style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
      <SectionHeader
        title="Action Items Due"
        count={!isLoading && items.length > 0 ? items.length : null}
        countClass="badge-red"
      />

      {isLoading ? (
        <LoadingBox />
      ) : items.length === 0 ? (
        <EmptyBox
          text="No upcoming action items"
          sub="Nothing due in the next 7 days."
          icon={
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M9 11l3 3L22 4"/>
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
            </svg>
          }
        />
      ) : (
        <div>
          {items.map((a, idx) => {
            const overdue = a.due_date && new Date(a.due_date + 'T00:00:00') < TODAY
            return (
              <div
                key={a.action_item_id}
                style={{ padding: '12px 20px', borderBottom: idx < items.length - 1 ? '1px solid var(--border)' : 'none' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  {a.assigned_user?.name && (
                    <span className="badge badge-gray" style={{ fontSize: '10px', flexShrink: 0 }}>
                      {a.assigned_user.name}
                    </span>
                  )}
                  <span style={{
                    fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{a.note}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.interaction?.subject_title || 'Interaction'}
                  </span>
                  <span
                    className={`badge ${overdue ? 'badge-red' : 'badge-gray'}`}
                    style={{ flexShrink: 0, fontSize: '10px', letterSpacing: '0.03em' }}
                  >
                    {overdue ? daysOverdue(a.due_date) : formatDate(a.due_date)}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

// ── Section: Recent Interactions ──────────────────────────────────────────

function RecentInteractions({ items, isLoading }) {
  return (
    <section className="card" style={{ padding: 0 }}>
      <SectionHeader title="Recent Interactions" />

      {isLoading ? (
        <LoadingBox />
      ) : items.length === 0 ? (
        <EmptyBox text="No interactions yet" />
      ) : (
        <div>
          {items.map((i, idx) => (
            <div
              key={i.interaction_id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '11px 20px',
                borderBottom: idx < items.length - 1 ? '1px solid var(--border)' : 'none',
              }}
            >
              <span className={`badge ${TYPE_BADGE[i.type] ?? 'badge-gray'}`} style={{ flexShrink: 0 }}>
                {i.type}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {i.subject_title || '(no subject)'}
                </div>
                {i.mandate && (
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                    {i.mandate.mandate_name}
                  </div>
                )}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', flexShrink: 0 }}>
                {formatDate(i.date)}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

// ── Section: Upcoming Meetings ────────────────────────────────────────────

function UpcomingMeetings({ items, isLoading }) {
  return (
    <section className="card" style={{ padding: 0 }}>
      <SectionHeader title="Upcoming Meetings" />

      {isLoading ? (
        <LoadingBox />
      ) : items.length === 0 ? (
        <EmptyBox text="No upcoming meetings" sub="Future calendar events will appear here." />
      ) : (
        <div>
          {items.map((m, idx) => (
            <div
              key={m.interaction_id}
              style={{
                padding: '12px 20px',
                borderBottom: idx < items.length - 1 ? '1px solid var(--border)' : 'none',
              }}
            >
              <div style={{
                fontSize: '13px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                marginBottom: '4px',
              }}>
                {m.subject_title || '(no title)'}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  {formatDateTime(m.date)}
                </span>
                {m.mandate && (
                  <span style={{
                    fontSize: '11px',
                    color: 'var(--text-muted)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '140px',
                  }}>
                    {m.mandate.mandate_name}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

// ── Shared micro-components ───────────────────────────────────────────────

function SectionHeader({ title, count, countClass = 'badge-gray' }) {
  return (
    <div style={{
      padding: '14px 20px',
      borderBottom: '1px solid var(--border)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexShrink: 0,
    }}>
      <h2 style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', letterSpacing: '0.01em' }}>
        {title}
      </h2>
      {count != null && (
        <span className={`badge ${countClass}`}>{count}</span>
      )}
    </div>
  )
}

function LoadingBox() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '160px' }}>
      <div className="spinner" />
    </div>
  )
}

function EmptyBox({ text, sub, icon }) {
  return (
    <div className="empty-state" style={{ padding: '36px 24px' }}>
      {icon && <div style={{ marginBottom: '12px', color: 'var(--text-muted)' }}>{icon}</div>}
      <h3 style={{ fontSize: '13px' }}>{text}</h3>
      {sub && <p style={{ marginTop: '4px', fontSize: '12px' }}>{sub}</p>}
    </div>
  )
}

// ── Main Dashboard ────────────────────────────────────────────────────────

export default function Dashboard() {
  // ── Fetch: active mandates
  const {
    data: mandatesData,
    isLoading: mandatesLoading,
  } = useQuery({
    queryKey: ['mandates', { status: 'Active' }],
    queryFn: () => api.get('/api/mandates', { params: { status: 'Active' } }).then(r => r.data.data ?? []),
  })

  // ── Fetch: all interactions (used for follow-ups + recent)
  const {
    data: interactionsData,
    isLoading: interactionsLoading,
  } = useQuery({
    queryKey: ['interactions', 'all'],
    queryFn: () => api.get('/api/interactions').then(r => r.data.data ?? []),
  })

  // ── Fetch: meetings only (for upcoming meetings panel)
  const {
    data: meetingsData,
    isLoading: meetingsLoading,
  } = useQuery({
    queryKey: ['interactions', { type: 'Meeting' }],
    queryFn: () => api.get('/api/interactions', { params: { type: 'Meeting' } }).then(r => r.data.data ?? []),
  })

  // ── Fetch: upcoming action items (due within 7 days, incomplete)
  const {
    data: upcomingActionItemsData,
    isLoading: actionItemsLoading,
  } = useQuery({
    queryKey: ['action-items', 'upcoming'],
    queryFn: () => api.get('/api/action-items/upcoming').then(r => r.data.data ?? []),
  })

  const mandates     = mandatesData     ?? []
  const interactions = interactionsData ?? []
  const meetings     = meetingsData     ?? []
  const upcomingActionItems = upcomingActionItemsData ?? []

  // ── Derived data ──────────────────────────────────────────────────────

  // Follow-up alerts: follow_up_due <= today (date comparison only)
  const followUps = interactions
    .filter(i => {
      if (!i.follow_up_due) return false
      const due = new Date(i.follow_up_due)
      due.setHours(0, 0, 0, 0)
      return due <= TODAY
    })
    .sort((a, b) => new Date(a.follow_up_due) - new Date(b.follow_up_due))

  // Recent interactions: already sorted date desc by the API — take first 5
  const recentInteractions = interactions.slice(0, 5)

  // Upcoming meetings: date >= start of today, next 5 chronologically
  const upcomingMeetings = meetings
    .filter(m => m.date && new Date(m.date) >= TODAY)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 5)

  // ── Stat strip ────────────────────────────────────────────────────────
  const stats = [
    { label: 'Active Mandates',       value: mandatesLoading      ? '…' : mandates.length },
    { label: 'Overdue Follow-ups',    value: interactionsLoading  ? '…' : followUps.length,    accent: followUps.length > 0 },
    { label: 'Upcoming Meetings',     value: meetingsLoading      ? '…' : upcomingMeetings.length },
    { label: 'Interactions (all)',    value: interactionsLoading  ? '…' : interactions.length },
  ]

  return (
    <div className="page">

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="page-header">
        <div className="page-header-text">
          <h1>Dashboard</h1>
          <p>At-a-glance: active mandates, overdue follow-ups, and recent activity.</p>
        </div>
      </div>

      {/* ── Stat strip ─────────────────────────────────────────────── */}
      <div className="stat-grid">
        {stats.map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div
              className="stat-value"
              style={s.accent ? { color: 'var(--danger)' } : {}}
            >
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* ── Row 1: Active Mandates (wider) + Follow-up Alerts ──────── */}
      <div className="dashboard-row-1">
        <ActiveMandates mandates={mandates} isLoading={mandatesLoading} />
        <FollowUpAlerts items={followUps} isLoading={interactionsLoading} />
      </div>

      {/* ── Row 2: Recent Interactions + Upcoming Meetings ─────────── */}
      <div className="dashboard-row-2">
        <RecentInteractions items={recentInteractions} isLoading={interactionsLoading} />
        <UpcomingMeetings   items={upcomingMeetings}   isLoading={meetingsLoading} />
      </div>

      {/* ── Row 3: Upcoming Action Items ───────────────────────────── */}
      <div className="dashboard-row-2" style={{ marginTop: 16 }}>
        <UpcomingActionItems items={upcomingActionItems} isLoading={actionItemsLoading} />
      </div>

    </div>
  )
}
